import * as React from 'react';
import {
    FieldContains,
    FieldCornerStyle,
    FieldDirection,
    FieldSize,
    FieldTextAlign,
    FieldValidationStatus,
    FieldVariant,
    ResponsiveStyleValue,
    useComponentDefaultProps,
    useHoneycombTheme,
} from '../../theming';
import { inDOM } from '../../utils';
import { SvgIconProps } from '../SvgIcon/SvgIcon';
import { FieldBaseContext, FieldBaseContextValues } from './FieldBase.context';
import { useId } from '../../hooks';

import { STATIC_STYLES, useDynamicStyles } from './FieldBase.styles';
import { IconButton } from '../IconButton/IconButton';
import { CrossIcon } from '../../icons';
import { ChevronDownIcon } from '../../icons/ChevronDownIcon';
import { Styler, StylerProps } from '../Styler/Styler';
import { ChipProps } from '../Chip';

export interface FieldBaseProps extends StylerProps {
    /**
     * The child control.
     */
    children: React.ReactElement<any>;

    /**
     * The icon for a button which will be placed at the end of the field and perform an action when clicked.
     */
    buttonIcon?: React.ReactElement<SvgIconProps>;

    /**
     * If `true`, the component will be disabled.
     * @default false
     */
    disabled?: boolean;

    /**
     * If `true`, the button will span the width of its container.
     * @default false
     */
    fullWidth?: boolean;

    /**
     * The text which will be used for the field's label.
     */
    label?: string;

    /**
     * The id of the `input` or `select` element.
     */
    id?: string;

    /**
     * @ignore
     */
    contains?: FieldContains;

    /**
     * The field's corner style. It might be preferable to set this on the theme using default props.
     * @default rounded
     */
    cornerStyle?: FieldCornerStyle;

    /**
     * The size of the field. This will primarily affect the field's height.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default m
     */
    size?: ResponsiveStyleValue<FieldSize>;

    /**
     * The alignment of the text.
     * @default start
     */
    textAlign?: FieldTextAlign;

    /**
     * The direction of the input. This can be used to override the `dir` attribute on the document.
     */
    direction?: FieldDirection;

    /**
     * Use this to forcibly shrink the label in the field.
     * Under normal circumstances this will automatically apply if the field has a value or placeholder, or if it is focused.
     * There may be situations where the value is autofilled and this needs to be forced.
     */
    shrinkLabel?: boolean;

    /**
     * The style variant of the field.
     * @default outlined
     */
    variant?: FieldVariant;

    /**
     * @ignore
     */
    focused?: boolean;

    /**
     * If `true`, a clear button will be displayed at the end of the field.
     */
    showClear?: boolean;

    /**
     * Callback fired when the button is clicked.
     */
    onButtonClick?: (event: React.SyntheticEvent) => void;

    /**
     * A reference passed to the `button` element.
     */
    buttonRef?: React.Ref<HTMLButtonElement>;

    /**
     * The width of the field.
     * @default auto
     */
    width?: ResponsiveStyleValue<number | React.CSSProperties['width']>;

    /**
     * The validation state. This will highlight the field in a particular colour.
     * @default none
     */
    validationStatus?: FieldValidationStatus;

    /**
     * An svg icon component. This is shortcut for applying an icon as a startDecorator.
     */
    icon?: React.ReactElement<SvgIconProps>;

    /**
     * Content which will adorn the start portion of the field.
     */
    startDecorator?: React.ReactElement;

    /**
     * Content which will adorn the end portion of the field.
     */
    endDecorator?: React.ReactElement;

    /**
     * Any array of `Chip` components which can represent selected options for an autocompleter.
     */
    chips?: Array<React.ReactElement<ChipProps>>;
}

const defaultProps: Partial<FieldBaseProps> = {
    cornerStyle: 'rounded',
    disabled: false,
    fullWidth: false,
    size: 'm',
    variant: 'outlined',
    contains: 'input',
    showClear: false,
    width: 'auto',
};

export function FieldBase(props: Readonly<FieldBaseProps>): React.JSX.Element {
    const {
        buttonIcon,
        textAlign = 'start',
        direction,
        children,
        contains = 'input',
        cornerStyle = 'rounded',
        disabled = false,
        fullWidth = false,
        id: idProp,
        label,
        shrinkLabel: shrinkLabelProp,
        size = 'm',
        variant = 'outlined',
        focused: focusedProp,
        onButtonClick,
        buttonRef,
        showClear,
        width = 'auto',
        validationStatus = 'none',
        icon,
        startDecorator,
        endDecorator,
        chips,
        ref,
        ...other
    } = useComponentDefaultProps('FieldBase', defaultProps, props);

    const isClient = inDOM();

    const { theme } = useHoneycombTheme();
    const componentTheme = theme.components.themes.FieldBase;

    const id = useId(idProp);
    const control = React.Children.only(children);

    const { value, defaultValue, placeholder } = control.props;

    const [focused, setFocused] = React.useState<boolean>(focusedProp ?? false);
    const [filled, setFilled] = React.useState<boolean>(Boolean(value || defaultValue));

    const controlRef = React.useRef<HTMLElement | null>(null);

    const withChips = (chips || []).length > 0;

    // We don't want to shrink the label on focus for button fields (as you can't type in them)
    const shrinkLabel =
        shrinkLabelProp || (focused && contains !== 'button') || withChips || filled || Boolean(placeholder);

    const withButton = showClear || Boolean(buttonIcon);

    const withStartDecorator = Boolean(startDecorator);
    const withEndDecorator = withButton;

    const dynamicStyles = useDynamicStyles({
        textAlign,
        size,
        cornerStyle,
        direction,
    });

    React.useEffect(() => {
        if (disabled) {
            setFocused(false);
            return;
        }
        if (focusedProp !== undefined) {
            setFocused(focusedProp);
        }
    }, [disabled, focusedProp]);

    const handleButtonMouseDown: React.MouseEventHandler<HTMLButtonElement> = (event): void => {
        // Retain focus on the input
        event.preventDefault();
    };

    // Clicking anywhere on the root element should focus the input
    const handleClick = (event: React.MouseEvent) => {
        if (controlRef.current && event.currentTarget === event.target) {
            if (contains === 'button') {
                controlRef.current.click();
            }
            controlRef.current.focus();
        }
    };

    const handleControlRef = React.useCallback(
        (element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement | null) => {
            if (!isClient || !element) {
                return;
            }
            controlRef.current = element;
        },
        [isClient]
    );

    const contextValues: FieldBaseContextValues = React.useMemo(
        () => ({
            setFocused,
            setFilled,
            controlRef: handleControlRef,
        }),
        [handleControlRef]
    );

    return (
        <FieldBaseContext.Provider value={contextValues}>
            <Styler
                ref={ref}
                baseStyles={[STATIC_STYLES, dynamicStyles]}
                w={width}
                data-field-base={true}
                data-variant={variant}
                data-contains={contains}
                data-status={validationStatus !== 'none' ? validationStatus : undefined}
                data-focused={focused || undefined}
                data-disabled={disabled || undefined}
                data-full-width={fullWidth || undefined}
                data-with-label={Boolean(label) || undefined}
                data-with-chips={withChips || undefined}
                data-with-start-decorator={withStartDecorator || undefined}
                data-with-end-decorator={withEndDecorator || undefined}
                onClick={handleClick}
                {...other}
            >
                {(startDecorator || icon) && (
                    <div data-start-decorator={true}>
                        {icon &&
                            React.cloneElement(icon, {
                                'data-icon': true,
                                ...icon.props,
                            })}
                        {startDecorator}
                    </div>
                )}

                <Styler data-inner={true} onClick={handleClick}>
                    {label && (
                        <label data-label={true} data-shrink={shrinkLabel ? true : undefined} htmlFor={id || undefined}>
                            {label}
                        </label>
                    )}

                    {control &&
                        contains !== 'Iframe' &&
                        React.cloneElement(control, {
                            id,
                            'data-control': true,
                        })}
                    {control &&
                        contains === 'Iframe' &&
                        React.cloneElement(control, {
                            'data-iframe': true,
                        })}

                    {contains === 'select' && <ChevronDownIcon data-select-indicator={true} />}

                    {withChips && <div data-chips={true}>{chips?.map((chip) => chip)}</div>}
                </Styler>

                {withButton && (
                    <div data-end-decorator={true}>
                        <IconButton
                            ref={buttonRef}
                            size={showClear ? 'xs' : componentTheme.endButtonSize}
                            alignEdge="end"
                            onClick={onButtonClick}
                            onMouseDown={handleButtonMouseDown}
                            shape="circle"
                            disabled={disabled}
                            data-button={true}
                        >
                            {buttonIcon || <CrossIcon />}
                        </IconButton>
                    </div>
                )}
            </Styler>
        </FieldBaseContext.Provider>
    );
}
